import {
  map, find, omit, isEqual,
  cloneDeep, forEach, reduce, filter,
  has, includes, sortBy, some,
} from 'lodash';
import { format, zonedTimeToUtc } from 'date-fns-tz';
import {
  checkIfRequiredFieldsEntered,
  mapPlayerSetupData,
} from './player-setup-mapper';
import marketMapper from './market-mapper';
import sportIds from './sports';
import { getParamVariantValue, hasParamVariant } from './param-variant-markets';

const {
  BASKETBALL_ID,
  SOCCER_ID,
} = sportIds;
const PLAYER_FIRST_SCORER_MARKET_CODE = 'PLAYER_FIRST_SCORER';

export const MARKET_TIER_MIN_VALUE = 1;
export const MARKET_TIER_MAX_VALUE = 4;

const mergePlayerWithEventMarkets = (playerMarkets, eventMarkets, playerId) => map(
  playerMarkets,
  (playerMarket) => {
    const marketsByCode = filter(eventMarkets, (market) => market.marketCode === playerMarket.marketCode);
    if (some(marketsByCode, (market) => hasParamVariant(market.marketType?.params))) {
      return {
        ...playerMarket,
        currentMarket: reduce(marketsByCode, (markets, market) => ({
          ...markets,
          [getParamVariantValue(market.marketType?.params)]: market,
        }), {}),
        availableParamVariants: sortBy(reduce(marketsByCode, (variants, market) => {
          const variantValue = getParamVariantValue(market.marketType?.params);
          return variantValue != null ? [...variants, variantValue] : variants;
        }, []), (variant) => variant),
        isParamVariantMarket: true,
        marketCode: playerMarket.marketCode,
      };
    }

    return {
      ...playerMarket,
      currentMarket: find(eventMarkets, { marketCode: playerMarket.marketCode, marketSummary: { playerId } }),
    };
  },
);

export const mapPlayerProps = (playerPropsData, squadData, event, playerMarketsFromEvent) => {
  if (!playerPropsData || !squadData || !event) return [];

  const { homeTeam, awayTeam } = playerPropsData;
  const { homeSquad, awaySquad } = squadData;
  const { isUsaView, competitors } = event;

  const teamASquadData = isUsaView ? awaySquad : homeSquad;
  const teamBSquadData = isUsaView ? homeSquad : awaySquad;

  const teamASquad = map(teamASquadData.squad, (squad) => squad.player);
  const teamBSquad = map(teamBSquadData.squad, (squad) => squad.player);

  let tempTeamA = isUsaView ? awayTeam : homeTeam;
  let tempTeamB = isUsaView ? homeTeam : awayTeam;

  tempTeamA = map(tempTeamA?.playersParams, (player) => ({
    ...player?.playerParams,
    lineup: player?.lineup,
    voided: player?.voided,
    marketCodeDetails: mergePlayerWithEventMarkets(player?.marketCodeDetails, playerMarketsFromEvent, player?.playerParams?.playerId),
    playerPositionIndex: player?.playerPositionIndex || 1,
    isPitcher: player.isPitcher,
    isHitter: player.isHitter,
    disabled: player?.disabled,
    isPlayerInCalculatedMode: player?.paramSource === 'CALCULATED',
    calculatedParamsAvailable: player?.calculatedParamsAvailable,
    calculatedParamsUpdatedAt: player?.calculatedParamsUpdatedAt ? format(zonedTimeToUtc(player?.calculatedParamsUpdatedAt, 'UTC'), 'hh:mm') : '',
    paramFindingErrorMessage: player?.paramFindingErrorMessage,
    paramSource: player?.paramSource,
  }));

  tempTeamB = map(tempTeamB?.playersParams, (player) => ({
    ...player?.playerParams,
    lineup: player?.lineup,
    voided: player?.voided,
    marketCodeDetails: mergePlayerWithEventMarkets(player?.marketCodeDetails, playerMarketsFromEvent, player?.playerParams?.playerId),
    playerPositionIndex: player?.playerPositionIndex || 1,
    isPitcher: player.isPitcher,
    isHitter: player.isHitter,
    disabled: player?.disabled,
    isPlayerInCalculatedMode: player?.paramSource === 'CALCULATED',
    calculatedParamsAvailable: player?.calculatedParamsAvailable,
    calculatedParamsUpdatedAt: player?.calculatedParamsUpdatedAt ? format(zonedTimeToUtc(player?.calculatedParamsUpdatedAt, 'UTC'), 'hh:mm') : '',
    paramFindingErrorMessage: player?.paramFindingErrorMessage,
    paramSource: player?.paramSource,
  }));

  const data = {
    teamA: {
      teamId: teamASquadData.teamId,
      name: find(competitors, { teamId: teamASquadData.teamId }).name,
    },
    teamB: {
      teamId: teamBSquadData.teamId,
      name: find(competitors, { teamId: teamBSquadData.teamId }).name,
    },
  };
  data.teamA.playerProps = map(tempTeamA, (player) => ({
    ...player,
    playerName: find(teamASquad, { playerId: player.playerId })?.personalInfo
      ?.name,
    marketCodeDetails: map(player.marketCodeDetails, (market) => ({
      ...market,
      marketName: marketMapper.playersMarketName(market.marketCode, event.sportId),
    })),
    isSuspended:
    player.marketCodeDetails.length
    && !filter(player.marketCodeDetails, (market) => !market.suspended)?.length,
    playerLocked: true,
  }));
  data.teamB.playerProps = map(tempTeamB, (player) => ({
    ...player,
    playerName: find(teamBSquad, { playerId: player.playerId })?.personalInfo
      ?.name,
    marketCodeDetails: map(player.marketCodeDetails, (market) => ({
      ...market,
      marketName: marketMapper.playersMarketName(market.marketCode, event.sportId),
    })),
    isSuspended:
    player.marketCodeDetails.length
    && !filter(player.marketCodeDetails, (market) => !market.suspended)?.length,
    playerLocked: true,
  }));
  const eventMapped = mapPlayerSetupData(event);
  if (eventMapped?.competitionType === 'NBA' || eventMapped?.competitionType === 'FIBA' || event?.sportId === SOCCER_ID) {
    data.playerComboMarketCodeDetails = reduce(
      playerPropsData?.playerComboMarketCodeDetails,
      (allMarkets, currentMarket, key) => {
        // Extract the other player index from the key
        const indices = key.match(/\((-?\d+),\s*(-?\d+)\)/);
        // eslint-disable-next-line no-unused-vars
        const [_, index1, index2] = indices;
        const markets = map(currentMarket, (market) => ({
          ...market,
          playersCombo: {
            first: parseInt(index1, 10),
            second: parseInt(index2, 10),
          },
          isLocked: false,
          isComboMarket: true,
          marketName: marketMapper.comboPlayersMarketName(market.marketCode),
          currentMarket: find(playerMarketsFromEvent, (marketFromEvent) => (
            market.marketCode === marketFromEvent.marketCode
            && marketFromEvent?.marketType?.params?.FIRST_PLAYER_INDEX === parseInt(index1, 10)
            && marketFromEvent?.marketType?.params?.SECOND_PLAYER_INDEX === parseInt(index2, 10)
          )),
        }));
        return {
          ...allMarkets,
          [key]: markets,
        };
      }, {},
    );
    // Check are players suspended based on the combo markets
    data.teamA.playerProps = map(data.teamA.playerProps, (player) => {
      let playerHasComboMarket = false;
      const isSuspendedInComboMarkets = Object.keys(data.playerComboMarketCodeDetails).every((key) => {
        if (key.includes(player.playerIndex.toString())) {
          playerHasComboMarket = true;
          const markets = data.playerComboMarketCodeDetails[key];
          return markets.length && markets.every((market) => market.suspended);
        }
        return true;
      });
      return {
        ...player,
        isSuspended: playerHasComboMarket ? isSuspendedInComboMarkets && player.isSuspended : player.isSuspended,
      };
    });
    data.teamB.playerProps = map(data.teamB.playerProps, (player) => {
      let playerHasComboMarket = false;
      const isSuspendedInComboMarkets = Object.keys(data.playerComboMarketCodeDetails).every((key) => {
        if (key.includes(player.playerIndex.toString())) {
          playerHasComboMarket = true;
          const markets = data.playerComboMarketCodeDetails[key];
          return markets.length && markets.every((market) => market.suspended);
        }
        return true;
      });
      return {
        ...player,
        isSuspended: playerHasComboMarket ? isSuspendedInComboMarkets && player.isSuspended : player.isSuspended,
      };
    });
  }
  return data;
};

export const mapTradingPlayerPropsPublishPayloadByTeam = (teamData) => map(teamData, (player) => ({
  playerParams: { ...omit(player, ['marketCodeDetails', 'lineup', 'voided', 'disabled', 'isHitter', 'isPitcher', 'playerPositionIndex']) },
  marketCodeDetails: player.marketCodeDetails,
  lineup: player.lineup,
  voided: player.voided,
  playerPositionIndex: player.playerPositionIndex,
  // Baseball params
  disabled: player.disabled,
  isHitter: player.isHitter,
  isPitcher: player.isPitcher,
  paramSource: player?.paramSource,
}));

export const mapTradingPlayerPropsPublishPayload = (tradingPlayerProps, tradingEvent, selectedParamSource = 'MANUAL') => {
  const payload = cloneDeep(tradingPlayerProps);
  const isUSAView = has(tradingEvent, 'isUSAView') ? tradingEvent.isUSAView : tradingEvent.isUsaView;
  const homeTeam = isUSAView ? payload.teamB : payload.teamA;
  const awayTeam = isUSAView ? payload.teamA : payload.teamB;

  return {
    homeTeam: {
      playersParams: mapTradingPlayerPropsPublishPayloadByTeam(homeTeam.playerProps),
    },
    awayTeam: {
      playersParams: mapTradingPlayerPropsPublishPayloadByTeam(awayTeam.playerProps),
    },
    selectedParamSource,
  };
};

export const mapTradingPlayerPropsPricesSimulatePayload = (tradingPlayerProps, tradingEvent) => {
  const payload = cloneDeep(tradingPlayerProps);
  const homeTeam = tradingEvent.isUsaView ? payload.teamB : payload.teamA;
  const awayTeam = tradingEvent.isUsaView ? payload.teamA : payload.teamB;
  const isBasketball = tradingEvent?.sportId === BASKETBALL_ID;
  homeTeam.players = map(homeTeam.playerProps, (player) => omit({
    ...player,
    marketCodes: map(
      filter(
        player.marketCodeDetails,
        ({ marketCode }) => !isBasketball || player.isStarter || marketCode !== PLAYER_FIRST_SCORER_MARKET_CODE,
      ),
      (market) => market.marketCode,
    ),
  }, ['marketCodeDetails']));
  awayTeam.players = map(awayTeam.playerProps, (player) => omit({
    ...player,
    marketCodes: map(
      filter(
        player.marketCodeDetails,
        ({ marketCode }) => !isBasketball || player.isStarter || marketCode !== PLAYER_FIRST_SCORER_MARKET_CODE,
      ),
      (market) => market.marketCode,
    ),
  }, ['marketCodeDetails']));

  const payloadMapped = {
    homeTeam: omit(homeTeam, ['name', 'teamId', 'playerProps']),
    awayTeam: omit(awayTeam, ['name', 'teamId', 'playerProps']),
  };
  const eventMapped = mapPlayerSetupData(tradingEvent);
  if (eventMapped?.competitionType === 'NBA' || eventMapped?.competitionType === 'FIBA' || tradingEvent?.sportId === SOCCER_ID) {
    payloadMapped.comboPlayerMarkets = reduce(
      tradingPlayerProps.playerComboMarketCodeDetails,
      (allMarkets, currentMarket, key) => {
        const markets = map(currentMarket, ({ marketCode }) => marketCode);
        return {
          ...allMarkets,
          [key]: markets,
        };
      }, {},
    );
  }

  return payloadMapped;
};

export const mapNewMarketPrices = (players) => reduce(
  players,
  (allPlayers, player) => {
    const marketsPerPlayer = [];
    forEach(player.markets, (market) => {
      marketsPerPlayer.push({
        selections: market?.selections,
        marketSummary: market?.marketSummary,
        marketType: market?.marketType,
      });
    });
    return [
      ...allPlayers,
      ...marketsPerPlayer,
    ];
  },
  [],
);

export const mergeNewMarketPrices = (allMarkets, updatedMarkets) => reduce(
  allMarkets,
  (markets, existingMarket) => {
    let updatedMarket = find(updatedMarkets, (market) => {
      const existingMarketParams = existingMarket?.marketType?.params;
      const updatedMarketParams = market?.marketType?.params;
      return (
        existingMarketParams.LINE === updatedMarketParams.LINE
        && existingMarketParams.SELECTIONS === updatedMarketParams.SELECTIONS
        && existingMarketParams.PLAYER_INDEX === updatedMarketParams.PLAYER_INDEX
      );
    });

    updatedMarket = updatedMarket ? { ...existingMarket, ...updatedMarket } : existingMarket;

    return [
      ...markets,
      ...[updatedMarket],
    ];
  },
  [],
);

const omitMarketsFieldFromTeamPlayer = (data) => {
  const { teamA, teamB } = cloneDeep(data);
  teamA.playerProps = map(teamA.playerProps, (player) => omit(player, ['marketCodeDetails']));
  teamB.playerProps = map(teamB.playerProps, (player) => omit(player, ['marketCodeDetails']));

  return {
    teamA,
    teamB,
  };
};

export const isTradingPlayerPropsStateChanged = (lastState, newState) => {
  const lastStateWithMarketsOmited = omitMarketsFieldFromTeamPlayer(lastState);
  const newStateWithMarketsOmited = omitMarketsFieldFromTeamPlayer(newState);
  return !isEqual(lastStateWithMarketsOmited, newStateWithMarketsOmited);
};
export const checkIsPublishBtnEnabled = (lastState, newState) => !isEqual(lastState, newState);

export const checkIsTradingTeamPropsModelValid = (model, sportId) => {
  const { teamA, teamB } = model;

  if (!checkIfRequiredFieldsEntered(teamA.playerProps, sportId)
  || !checkIfRequiredFieldsEntered(teamB.playerProps, sportId)) {
    return false;
  }
  return true;
};

export const mapSquadData = (teamA, teamB) => [
  ...map(teamA.squad, (info) => ({
    ...info.player,
    teamId: teamA.teamId,
  })),
  ...map(teamB.squad, (info) => ({
    ...info.player,
    teamId: teamB.teamId,
  })),
];

// Here we can add player props when we get the info about their range
export const checkIsTradingPropsModelValid = (model, sportId) => checkIsTradingTeamPropsModelValid(model, sportId);

export const footballParameters = () => ([
  {
    param: 'goalTouchdown',
    label: 'Goals',
    order: 1,
  },
  {
    param: 'redTouchdown',
    label: 'Red zone TD',
    order: 2,
  },
  {
    param: 'fieldTouchdown',
    label: 'Field TD',
    order: 3,
  },
  {
    param: 'rushLoad',
    label: 'Rush',
    order: 4,
  },
  {
    param: 'qbLoad',
    label: 'QB',
    order: 5,
  },
  {
    param: 'longReceptionLoad',
    label: 'Long Rec',
    order: 6,
  },
  {
    param: 'shortReceptionLoad',
    label: 'Short Rec',
    order: 7,
  },
]);

export const basketballParameters = () => ([
  {
    param: 'minutesUncertainty',
    label: 'Min Uncertainty',
    order: 1,
  },
  {
    param: 'timeQ1',
    label: 'Min Q1',
    order: 2,
    editableInCalculatedMode: true,
  },
  {
    param: 'timeQ2',
    label: 'Min Q2',
    order: 3,
    editableInCalculatedMode: true,
  },
  {
    param: 'timeQ3',
    label: 'Min Q3',
    order: 4,
    editableInCalculatedMode: true,
  },
  {
    param: 'timeQ4',
    label: 'Min Q4',
    order: 5,
    editableInCalculatedMode: true,
  },
  {
    param: 'points',
    label: 'Points',
    order: 6,
  },
  {
    param: 'threes',
    label: 'Threes',
    order: 7,
  },
  {
    param: 'rebounds',
    label: 'Rebounds',
    order: 8,
  },
  {
    param: 'assists',
    label: 'Assists',
    order: 9,
  },
  {
    param: 'quarter4UsageBoost.offensiveReboundPutbackBoost',
    label: 'ORB P Boost',
    order: 10,
  },
  {
    param: 'steals',
    label: 'Steals',
    order: 11,
  },
  {
    param: 'blocks',
    label: 'Blocks',
    order: 12,
  },
]);

export const hockeyParameters = () => ([
  {
    param: 'goals',
    label: 'Goals',
    order: 1,
  },
  {
    param: 'assists',
    label: 'Assists',
    order: 2,
  },
  {
    param: 'saves',
    label: 'Saves',
    order: 3,
  },
  {
    param: 'shotsOnGoal',
    label: 'Shots',
    order: 4,
  },
]);

export const soccerParameters = () => ([
  {
    param: 'goals',
    label: 'Goals',
    order: 1,
  },
  {
    param: 'minutes',
    label: 'Minutes',
    order: 2,
  },
  {
    param: 'assists',
    label: 'Assists',
    order: 3,
  },
  {
    param: 'shotsOnTarget',
    label: 'Shots on Target',
    order: 4,
  },
  {
    param: 'totalShots',
    label: 'Total Shots',
    order: 5,
  },
  {
    param: 'totalPasses',
    label: 'Total Passes',
    order: 6,
  },
  {
    param: 'accuratePasses',
    label: 'Accurate Passes',
    order: 7,
  },
  {
    param: 'tackles',
    label: 'Tackles',
    order: 8,
  },
  {
    param: 'interceptions',
    label: 'Interceptions',
    order: 9,
  },
  {
    param: 'offsides',
    label: 'Offsides',
    order: 10,
  },
  {
    param: 'fouls',
    label: 'Fouls',
    order: 11,
  },
  {
    param: 'saves',
    label: 'Saves',
    order: 12,
  },
  {
    param: 'accurateCrosses',
    label: 'Accurate Crosses',
    order: 13,
  },
  {
    param: 'totalCrosses',
    label: 'Total Crosses',
    order: 14,
  },
  {
    param: 'penalty',
    label: 'Penalty',
    order: 15,
  },
  {
    param: 'yellowCards',
    label: 'Yellow Cards',
    order: 16,
  },
  {
    param: 'redCards',
    label: 'Red Cards',
    order: 17,
  },
]);
